import React, { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import api from '../../../services/authService'
import { obtenerMensajeErrorApi } from '../../../services/apiError'
import { useAuth } from '../../../contextos/autenticacion/ContextoAutenticacion'
import toast from 'react-hot-toast'
import { 
  AcademicCapIcon, 
  UserGroupIcon, 
  CalendarIcon, 
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  PlayIcon,
  ArrowPathIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  SparklesIcon,
  ShieldCheckIcon,
  ArrowDownTrayIcon,
  CubeIcon,
  TagIcon
} from '@heroicons/react/24/outline'

// Tipos
interface Simulacion {
  id_simulacion: number
  titulo: string
  descripcion: string
  tipo: string
  nivel_dificultad: string
}

interface Grupo {
  id_grupo: number
  nombre: string
}

interface Asignacion {
  id_asignacion: number
  id_grupo: number
  nombre_grupo: string
  id_simulacion: number
  titulo_simulacion: string
  tipo_simulacion: string
  fecha_asignacion: string
  fecha_limite: string | null
  total_miembros: number
  sin_iniciar: number
  en_curso: number
  completado: number
  calificado: number
  promedio_avance: number
  promedio_calificacion: number | null
}

interface ResumenPorSimulacion {
  id_simulacion: number
  titulo_simulacion: string
  tipo_simulacion: string
  total_asignaciones: number
  total_grupos: number
  total_miembros: number
  sin_iniciar: number
  en_curso: number
  completado: number
  calificado: number
  promedio_avance: number
  promedio_calificacion: number | null
}

interface ResumenPorGrupo {
  id_grupo: number
  nombre_grupo: string
  total_asignaciones: number
  total_simulaciones: number
  total_miembros: number
  sin_iniciar: number
  en_curso: number
  completado: number
  calificado: number
  promedio_avance: number
  promedio_calificacion: number | null
}

interface MetricasGenerales {
  total_simulaciones_catalogo: number
  total_simulaciones_asignadas: number
  total_grupos: number
  total_grupos_con_asignaciones: number
  total_asignaciones: number
  total_miembros_asignados: number
  total_iniciados: number
  total_completados: number
  total_calificados: number
  promedio_avance: number
  promedio_calificacion: number | null
}

interface DashboardResumenGestion {
  vista: 'gestion'
  simulaciones: Simulacion[]
  grupos: Grupo[]
  seguimiento: {
    metricas_generales: MetricasGenerales
    resumen_por_simulacion: ResumenPorSimulacion[]
    resumen_por_grupo: ResumenPorGrupo[]
    asignaciones: Asignacion[]
  }
}

interface MiAsignacion {
  id_asignacion: number
  id_simulacion: number
  titulo: string
  descripcion: string
  tipo: string
  nivel_dificultad: string
  id_grupo: number
  nombre_grupo: string
  fecha_asignacion: string
  fecha_limite: string | null
  estado: string
  puntaje: number | null
  progreso?: number
}

interface ResumenAlumno {
  total_grupos: number
  total_asignaciones: number
  activas: number
  completadas: number
  promedio_calificacion: number | null
}

interface ConstitucionActiva {
  id_constitucion: number
  nombre: string
  descripcion?: string
  tipo?: string
  contacto_email?: string
  contacto_telefono?: string
  rol_miembro?: string
}

interface DashboardResumenAlumno {
  vista: 'alumno'
  constitucion: ConstitucionActiva
  grupos: Grupo[]
  mis_asignaciones: MiAsignacion[]
  resumen_alumno: ResumenAlumno
}

export const PaginaDashboardInstitucion: React.FC = () => {
  const { usuario } = useAuth()
  const isStudent = usuario?.rol === 'institucion_alumno' || usuario?.rol === 'institucion_usuario'

  const [activeTab, setActiveTab] = useState<'simulaciones' | 'seguimiento'>('simulaciones')
  
  // Estado Admin/Jefe/Maestro
  const [simulaciones, setSimulaciones] = useState<Simulacion[]>([])
  const [grupos, setGrupos] = useState<Grupo[]>([])
  const [asignaciones, setAsignaciones] = useState<Asignacion[]>([])
  const [dashboardResumen, setDashboardResumen] = useState<DashboardResumenGestion | null>(null)
  const [dashboardAlumno, setDashboardAlumno] = useState<DashboardResumenAlumno | null>(null)
  
  // Estado Alumno
  const [misAsignaciones, setMisAsignaciones] = useState<MiAsignacion[]>([])
  const [loading, setLoading] = useState(false)

  // Filtros y Búsqueda en Vista Docente
  const [busquedaCasos, setBusquedaCasos] = useState('')
  const [filtroTipoCaso, setFiltroTipoCaso] = useState<'TODOS' | 'Despacho' | 'Logistica' | 'Transporte'>('TODOS')
  const [filtroDificultadCaso, setFiltroDificultadCaso] = useState<'TODOS' | 'Principiante' | 'Intermedio' | 'Avanzado'>('TODOS')
  
  const [busquedaSeguimiento, setBusquedaSeguimiento] = useState('')
  const [filtroGrupoSeguimiento, setFiltroGrupoSeguimiento] = useState<string>('TODOS')

  // Filtros en Vista Alumno
  const [busquedaAlumno, setBusquedaAlumno] = useState('')
  const [filtroEstadoAlumno, setFiltroEstadoAlumno] = useState<'TODOS' | 'activa' | 'completado'>('TODOS')

  // Estado Modal Asignar
  const [showAsignarModal, setShowAsignarModal] = useState(false)
  const [selectedSimulacion, setSelectedSimulacion] = useState<Simulacion | null>(null)
  const [selectedGrupo, setSelectedGrupo] = useState<string>('')
  const [fechaLimite, setFechaLimite] = useState<string>('')

  // Estado Modal Crear Simulación
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newSimulacion, setNewSimulacion] = useState({
    titulo: '',
    descripcion: '',
    tipo: 'Despacho',
    nivel_dificultad: 'Intermedio'
  })

  const cargarDatos = async () => {
    setLoading(true)
    try {
      if (isStudent) {
        const dashboardRes = await api.get('/api/institucion/dashboard/resumen')
        setDashboardAlumno(dashboardRes.data)
        setMisAsignaciones(dashboardRes.data?.mis_asignaciones || [])
      } else {
        const dashboardRes = await api.get('/api/institucion/dashboard/resumen')
        setDashboardResumen(dashboardRes.data)
        setSimulaciones(dashboardRes.data?.simulaciones || [])
        setGrupos(dashboardRes.data?.grupos || [])
        setAsignaciones(dashboardRes.data?.seguimiento?.asignaciones || [])
      }
    } catch (error) {
      console.error('Error cargando datos del dashboard WMS:', error)
      toast.error('No se pudo sincronizar la telemetría del dashboard')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    cargarDatos()
  }, [isStudent])

  const metricasGenerales = dashboardResumen?.seguimiento.metricas_generales
  const resumenPorSimulacion = dashboardResumen?.seguimiento.resumen_por_simulacion || []
  const resumenPorGrupo = dashboardResumen?.seguimiento.resumen_por_grupo || []
  const resumenAlumno = dashboardAlumno?.resumen_alumno
  const gruposAlumno = dashboardAlumno?.grupos || []
  const constitucionAlumno = dashboardAlumno?.constitucion

  const formatearPromedio = (valor: number | null, sufijo = '') => {
    if (valor === null || Number.isNaN(valor)) return 'Sin datos'
    return `${valor.toFixed(1)}${sufijo}`
  }

  // Filtrado de Simulaciones (Docente)
  const simulacionesFiltradas = useMemo(() => {
    return simulaciones.filter(sim => {
      if (filtroTipoCaso !== 'TODOS' && sim.tipo !== filtroTipoCaso) return false
      if (filtroDificultadCaso !== 'TODOS' && sim.nivel_dificultad !== filtroDificultadCaso) return false
      if (!busquedaCasos.trim()) return true
      const q = busquedaCasos.toLowerCase().trim()
      return (
        sim.titulo.toLowerCase().includes(q) ||
        sim.descripcion.toLowerCase().includes(q) ||
        sim.tipo.toLowerCase().includes(q) ||
        sim.nivel_dificultad.toLowerCase().includes(q)
      )
    })
  }, [simulaciones, busquedaCasos, filtroTipoCaso, filtroDificultadCaso])

  // Filtrado de Asignaciones (Docente)
  const asignacionesFiltradas = useMemo(() => {
    return asignaciones.filter(asig => {
      if (filtroGrupoSeguimiento !== 'TODOS' && String(asig.id_grupo) !== filtroGrupoSeguimiento) return false
      if (!busquedaSeguimiento.trim()) return true
      const q = busquedaSeguimiento.toLowerCase().trim()
      return (
        asig.titulo_simulacion.toLowerCase().includes(q) ||
        asig.nombre_grupo.toLowerCase().includes(q) ||
        asig.tipo_simulacion.toLowerCase().includes(q)
      )
    })
  }, [asignaciones, busquedaSeguimiento, filtroGrupoSeguimiento])

  // Filtrado de Asignaciones (Alumno)
  const misAsignacionesFiltradas = useMemo(() => {
    return misAsignaciones.filter(asig => {
      if (filtroEstadoAlumno === 'activa' && asig.estado === 'completado') return false
      if (filtroEstadoAlumno === 'completado' && asig.estado !== 'completado') return false
      if (!busquedaAlumno.trim()) return true
      const q = busquedaAlumno.toLowerCase().trim()
      return (
        asig.titulo.toLowerCase().includes(q) ||
        asig.descripcion.toLowerCase().includes(q) ||
        asig.tipo.toLowerCase().includes(q) ||
        asig.nombre_grupo.toLowerCase().includes(q)
      )
    })
  }, [misAsignaciones, busquedaAlumno, filtroEstadoAlumno])

  const exportarSeguimientoCSV = () => {
    if (asignacionesFiltradas.length === 0) {
      toast.error('No hay asignaciones para exportar en el filtro actual')
      return
    }
    const headers = ['ID Asignacion', 'Caso Practico', 'Tipo', 'Grupo', 'Fecha Asignacion', 'Fecha Limite', 'Total Alumnos', 'Sin Iniciar', 'En Curso', 'Completados', 'Calificados', 'Avance %', 'Promedio']
    const rows = asignacionesFiltradas.map(a => [
      a.id_asignacion,
      `"${(a.titulo_simulacion || '').replace(/"/g, '""')}"`,
      `"${a.tipo_simulacion || ''}"`,
      `"${(a.nombre_grupo || '').replace(/"/g, '""')}"`,
      `"${new Date(a.fecha_asignacion).toLocaleDateString()}"`,
      `"${a.fecha_limite ? new Date(a.fecha_limite).toLocaleDateString() : 'Sin limite'}"`,
      a.total_miembros,
      a.sin_iniciar,
      a.en_curso,
      a.completado,
      a.calificado,
      `${a.promedio_avance.toFixed(1)}%`,
      a.promedio_calificacion !== null ? a.promedio_calificacion.toFixed(1) : 'N/A'
    ])

    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n')
    const encodedUri = encodeURI(csvContent)
    const link = document.createElement('a')
    link.setAttribute('href', encodedUri)
    link.setAttribute('download', `WMS_Auditoria_Academica_${new Date().toISOString().slice(0, 10)}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('Reporte académico WMS exportado con éxito')
  }

  const handleAsignar = async () => {
    if (!selectedSimulacion || !selectedGrupo) {
      toast.error('Por favor selecciona un grupo para asignar el caso')
      return
    }
    
    try {
      await api.post('/api/simulaciones/asignar', {
        id_grupo: parseInt(selectedGrupo),
        id_simulacion: selectedSimulacion.id_simulacion,
        fecha_limite: fechaLimite ? new Date(fechaLimite).toISOString() : null
      })
      setShowAsignarModal(false)
      cargarDatos()
      toast.success('Caso práctico asignado al grupo con éxito')
    } catch (error: unknown) {
      toast.error(obtenerMensajeErrorApi(error, 'Error al asignar el caso'))
    }
  }

  const handleCreate = async () => {
    if (!newSimulacion.titulo.trim() || !newSimulacion.tipo || !newSimulacion.nivel_dificultad) {
      toast.error('Por favor completa todos los campos requeridos')
      return
    }

    try {
      await api.post('/api/simulaciones/crear', {
        titulo: newSimulacion.titulo.trim(),
        descripcion: newSimulacion.descripcion.trim(),
        tipo: newSimulacion.tipo,
        nivel_dificultad: newSimulacion.nivel_dificultad,
        datos_caso: {}
      })
      setShowCreateModal(false)
      setNewSimulacion({ titulo: '', descripcion: '', tipo: 'Despacho', nivel_dificultad: 'Intermedio' })
      cargarDatos()
      toast.success('Caso práctico WMS creado correctamente')
    } catch (error: unknown) {
      toast.error(obtenerMensajeErrorApi(error, 'Error al crear caso práctico'))
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este caso práctico? Esta acción no se puede deshacer.')) {
      return
    }

    try {
      await api.delete(`/api/simulaciones/${id}`)
      toast.success('Caso práctico eliminado del catálogo')
      cargarDatos()
    } catch (error: unknown) {
      toast.error(obtenerMensajeErrorApi(error, 'Error al eliminar el caso práctico'))
    }
  }

  // ==========================================
  // VISTA DEL ALUMNO (COCKPIT DE OPERACIONES WMS)
  // ==========================================
  if (isStudent) {
    return (
      <div className="space-y-8 animate-fadeIn max-w-[1600px] mx-auto pb-12">
        {/* Hero Header Bento Alumno */}
        <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[28px] p-6 md:p-8 border border-slate-200/80 dark:border-white/[0.08] shadow-xl relative overflow-hidden">
          <div className="pointer-events-none absolute -right-20 -top-20 w-80 h-80 rounded-full bg-blue-500/10 blur-3xl" />
          <div className="pointer-events-none absolute right-40 -bottom-20 w-60 h-60 rounded-full bg-indigo-500/10 blur-3xl" />

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold tracking-wide mb-3">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                TURNO LOGÍSTICO ACTIVO · MODO PRÁCTICA WMS
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
                Consola de Prácticas Profesionales
              </h1>
              <p className="text-slate-500 dark:text-zinc-400 text-sm max-w-2xl font-medium mt-1">
                {constitucionAlumno?.nombre || 'Centro Institucional de Simulación Logística'} &middot; {gruposAlumno.length} grupos matriculados
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={cargarDatos}
                disabled={loading}
                className="p-3 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-300 border border-slate-200/80 dark:border-white/10 transition-all shadow-sm group"
                title="Actualizar telemetría de prácticas"
              >
                <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* 4 Hero KPI Bento Cards Alumno */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {/* Card 1: Total Asignaciones */}
          <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] p-5 sm:p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-sm relative overflow-hidden group hover:border-blue-500/40 transition-all">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest">Casos Asignados</span>
              <div className="p-2.5 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
                <AcademicCapIcon className="h-5 w-5" />
              </div>
            </div>
            <p className="mt-4 text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
              {resumenAlumno?.total_asignaciones ?? misAsignaciones.length}
            </p>
            <p className="mt-2 text-xs text-slate-500 dark:text-zinc-400 font-medium">
              Simulaciones en tu plan formativo
            </p>
          </div>

          {/* Card 2: Casos Activos */}
          <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] p-5 sm:p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-sm relative overflow-hidden group hover:border-amber-500/40 transition-all">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest">En Ejecución</span>
              <div className="p-2.5 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
                <ClockIcon className="h-5 w-5" />
              </div>
            </div>
            <p className="mt-4 text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
              {resumenAlumno?.activas ?? misAsignaciones.filter(a => a.estado !== 'completado').length}
            </p>
            <p className="mt-2 text-xs text-slate-500 dark:text-zinc-400 font-medium">
              Retos pendientes por resolver
            </p>
          </div>

          {/* Card 3: Completadas */}
          <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] p-5 sm:p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-sm relative overflow-hidden group hover:border-emerald-500/40 transition-all">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest">Completadas</span>
              <div className="p-2.5 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                <CheckCircleIcon className="h-5 w-5" />
              </div>
            </div>
            <p className="mt-4 text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
              {resumenAlumno?.completadas ?? misAsignaciones.filter(a => a.estado === 'completado').length}
            </p>
            <p className="mt-2 text-xs text-slate-500 dark:text-zinc-400 font-medium">
              Operaciones finalizadas con éxito
            </p>
          </div>

          {/* Card 4: Promedio */}
          <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] p-5 sm:p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-sm relative overflow-hidden group hover:border-indigo-500/40 transition-all">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest">Score de Precisión</span>
              <div className="p-2.5 rounded-2xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                <ChartBarIcon className="h-5 w-5" />
              </div>
            </div>
            <p className="mt-4 text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
              {formatearPromedio(resumenAlumno?.promedio_calificacion ?? null)}
            </p>
            <p className="mt-2 text-xs text-slate-500 dark:text-zinc-400 font-medium">
              Calificación media auditada
            </p>
          </div>
        </div>

        {/* Barra de Filtros Alumno */}
        <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] p-4 border border-slate-200/80 dark:border-white/[0.08] shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="relative w-full md:w-96">
            <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={busquedaAlumno}
              onChange={(e) => setBusquedaAlumno(e.target.value)}
              placeholder="Buscar simulación o grupo..."
              className="w-full pl-10 pr-9 py-2 rounded-xl text-xs font-medium bg-slate-100 dark:bg-white/5 border border-slate-200/80 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
            />
            {busquedaAlumno && (
              <button
                onClick={() => setBusquedaAlumno('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-white"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-1.5 p-1 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200/60 dark:border-white/5 overflow-x-auto w-full md:w-auto">
            <button
              onClick={() => setFiltroEstadoAlumno('TODOS')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                filtroEstadoAlumno === 'TODOS'
                  ? 'bg-white dark:bg-white/10 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              Todos ({misAsignaciones.length})
            </button>
            <button
              onClick={() => setFiltroEstadoAlumno('activa')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                filtroEstadoAlumno === 'activa'
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'text-slate-500 dark:text-zinc-400 hover:text-amber-500'
              }`}
            >
              En Curso
            </button>
            <button
              onClick={() => setFiltroEstadoAlumno('completado')}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                filtroEstadoAlumno === 'completado'
                  ? 'bg-emerald-500 text-white shadow-sm'
                  : 'text-slate-500 dark:text-zinc-400 hover:text-emerald-500'
              }`}
            >
              Completadas
            </button>
          </div>
        </div>

        {/* Grid de Asignaciones del Alumno */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {misAsignacionesFiltradas.map((asignacion) => {
            const esCompletada = asignacion.estado === 'completado'
            const esEnCurso = asignacion.estado === 'en_curso'
            
            return (
              <div 
                key={asignacion.id_asignacion} 
                className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] border border-slate-200/80 dark:border-white/[0.08] shadow-md hover:shadow-2xl hover:border-blue-500/40 dark:hover:border-blue-500/30 transition-all duration-300 p-6 flex flex-col justify-between group"
              >
                <div>
                  {/* Fila superior: Tipo y Estado */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                      <CubeIcon className="h-3.5 w-3.5" />
                      {asignacion.tipo}
                    </span>

                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-[10px] font-bold border ${
                      esCompletada
                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                        : esEnCurso
                        ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                        : 'bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-zinc-400 border-slate-200 dark:border-white/10'
                    }`}>
                      {esCompletada ? <CheckCircleIcon className="h-3.5 w-3.5" /> : <ClockIcon className="h-3.5 w-3.5" />}
                      <span className="capitalize">{asignacion.estado.replace('_', ' ')}</span>
                    </span>
                  </div>

                  <h3 className="mt-4 text-lg font-black text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {asignacion.titulo}
                  </h3>

                  <p className="mt-2 text-xs text-slate-500 dark:text-zinc-400 line-clamp-3 leading-relaxed">
                    {asignacion.descripcion}
                  </p>

                  <div className="mt-4 pt-3 border-t border-slate-100 dark:border-white/[0.04] flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400">
                    <span className="flex items-center gap-1 font-semibold">
                      <UserGroupIcon className="h-3.5 w-3.5 text-slate-400" />
                      {asignacion.nombre_grupo}
                    </span>
                    {asignacion.fecha_limite && (
                      <span className="flex items-center gap-1 text-rose-500 dark:text-rose-400 font-bold text-[11px]">
                        <ExclamationCircleIcon className="h-3.5 w-3.5" />
                        Vence: {new Date(asignacion.fecha_limite).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  {asignacion.puntaje !== null && (
                    <div className="mt-3 p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-between">
                      <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300">Calificación Obtenida</span>
                      <span className="text-base font-black font-mono text-emerald-600 dark:text-emerald-400">
                        {asignacion.puntaje}/100
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-4 border-t border-slate-100 dark:border-white/[0.04]">
                  <Link
                    to={`/institucion/simulacion/caso-a/${asignacion.id_simulacion}`}
                    className={`w-full py-2.5 px-4 rounded-xl text-xs font-black flex items-center justify-center gap-2 shadow-md transition-all ${
                      esCompletada
                        ? 'bg-slate-100 hover:bg-slate-200 dark:bg-white/10 dark:hover:bg-white/15 text-slate-700 dark:text-white'
                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-blue-500/25'
                    }`}
                  >
                    <PlayIcon className="h-4 w-4" />
                    <span>{esCompletada ? 'Repasar Operación' : esEnCurso ? 'Continuar Turno' : 'Iniciar Simulación'}</span>
                  </Link>
                </div>
              </div>
            )
          })}

          {misAsignacionesFiltradas.length === 0 && !loading && (
            <div className="col-span-full py-16 text-center bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[28px] border border-dashed border-slate-300 dark:border-white/10 p-8">
              <div className="w-12 h-12 rounded-full bg-blue-500/10 text-blue-500 flex items-center justify-center mx-auto mb-3">
                <AcademicCapIcon className="h-6 w-6" />
              </div>
              <h4 className="text-base font-bold text-slate-900 dark:text-white">Sin asignaciones en esta vista</h4>
              <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1 max-w-sm mx-auto">
                No tienes casos prácticos que coincidan con el filtro actual o tu profesor aún no te ha asignado un caso.
              </p>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ====================================================
  // VISTA DOCENTE / JEFE / ADMIN (COMMAND CENTER INSTITUCIONAL)
  // ====================================================
  return (
    <div className="space-y-8 animate-fadeIn max-w-[1600px] mx-auto pb-12">
      {/* Header Glassmórfico de la Consola Docente */}
      <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[28px] p-6 md:p-8 border border-slate-200/80 dark:border-white/[0.08] shadow-xl relative overflow-hidden">
        <div className="pointer-events-none absolute -right-20 -top-20 w-80 h-80 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="pointer-events-none absolute right-40 -bottom-20 w-60 h-60 rounded-full bg-indigo-500/10 blur-3xl" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-bold tracking-wide mb-3">
              <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              SISTEMA WMS INSTITUCIONAL · PANEL DE CONTROL DOCENTE
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
              Dashboard Operativo & Casos Prácticos
            </h1>
            <p className="text-slate-500 dark:text-zinc-400 text-sm max-w-2xl font-medium mt-1">
              Diseña escenarios logísticos de grado industrial, asigna desafíos por cohorte y audita el desempeño laboral en tiempo real.
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={cargarDatos}
              disabled={loading}
              className="p-3 rounded-2xl bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-300 border border-slate-200/80 dark:border-white/10 transition-all shadow-sm group"
              title="Actualizar telemetría"
            >
              <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-xs sm:text-sm shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Crear Caso Práctico</span>
            </button>
          </div>
        </div>
      </div>

      {/* 4 Hero Bento Telemetry Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {/* Card 1: Casos en Catálogo */}
        <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] p-5 sm:p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-sm relative overflow-hidden group hover:border-blue-500/40 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest">Catálogo de Casos</span>
            <div className="p-2.5 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
              <AcademicCapIcon className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-4 text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
            {metricasGenerales?.total_simulaciones_catalogo ?? simulaciones.length}
          </p>
          <p className="mt-2 text-xs text-slate-500 dark:text-zinc-400 font-medium">
            {metricasGenerales?.total_simulaciones_asignadas ?? 0} con asignación activa
          </p>
        </div>

        {/* Card 2: Miembros Asignados */}
        <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] p-5 sm:p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-sm relative overflow-hidden group hover:border-indigo-500/40 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest">Alumnos Asignados</span>
            <div className="p-2.5 rounded-2xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
              <UserGroupIcon className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-4 text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
            {metricasGenerales?.total_miembros_asignados ?? 0}
          </p>
          <p className="mt-2 text-xs text-slate-500 dark:text-zinc-400 font-medium">
            En {metricasGenerales?.total_grupos_con_asignaciones ?? grupos.length} grupos académicos
          </p>
        </div>

        {/* Card 3: Avance Global */}
        <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] p-5 sm:p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-sm relative overflow-hidden group hover:border-amber-500/40 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest">Avance Promedio</span>
            <div className="p-2.5 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <ClockIcon className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-4 text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
            {formatearPromedio(metricasGenerales?.promedio_avance ?? 0, '%')}
          </p>
          <div className="mt-3 w-full bg-slate-100 dark:bg-white/5 rounded-full h-1.5 overflow-hidden">
            <div 
              className="bg-amber-500 h-1.5 rounded-full transition-all duration-700" 
              style={{ width: `${Math.min(100, Math.max(0, metricasGenerales?.promedio_avance ?? 0))}%` }} 
            />
          </div>
        </div>

        {/* Card 4: Calificación Global */}
        <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] p-5 sm:p-6 border border-slate-200/80 dark:border-white/[0.08] shadow-sm relative overflow-hidden group hover:border-emerald-500/40 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest">Score de Rendimiento</span>
            <div className="p-2.5 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <CheckCircleIcon className="h-5 w-5" />
            </div>
          </div>
          <p className="mt-4 text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
            {formatearPromedio(metricasGenerales?.promedio_calificacion ?? null)}
          </p>
          <p className="mt-2 text-xs text-slate-500 dark:text-zinc-400 font-medium">
            {metricasGenerales?.total_calificados ?? 0} evaluaciones asentadas
          </p>
        </div>
      </div>

      {/* Navegación por pestañas Bento */}
      <div className="flex items-center gap-2 p-1.5 bg-slate-100/90 dark:bg-white/[0.03] backdrop-blur-xl rounded-2xl border border-slate-200/70 dark:border-white/[0.06] w-fit shadow-sm">
        <button
          onClick={() => setActiveTab('simulaciones')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${
            activeTab === 'simulaciones'
              ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-md'
              : 'text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          <AcademicCapIcon className="h-4 w-4" />
          <span>Catálogo de Casos</span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-blue-50 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400">
            {simulaciones.length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('seguimiento')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${
            activeTab === 'seguimiento'
              ? 'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-md'
              : 'text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          <ChartBarIcon className="h-4 w-4" />
          <span>Seguimiento y Resultados</span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-indigo-50 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
            {asignaciones.length}
          </span>
        </button>
      </div>

      {/* TAB 1: CATÁLOGO DE CASOS PRÁCTICOS */}
      {activeTab === 'simulaciones' && (
        <div className="space-y-6">
          {/* Barra de Búsqueda y Filtros de Casos */}
          <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] p-4 border border-slate-200/80 dark:border-white/[0.08] shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="relative w-full md:w-96">
              <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={busquedaCasos}
                onChange={(e) => setBusquedaCasos(e.target.value)}
                placeholder="Buscar por título, dificultad o tipo..."
                className="w-full pl-10 pr-9 py-2 rounded-xl text-xs font-medium bg-slate-100 dark:bg-white/5 border border-slate-200/80 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
              />
              {busquedaCasos && (
                <button
                  onClick={() => setBusquedaCasos('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-white"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto flex-wrap">
              {/* Filtro por Tipo */}
              <div className="flex items-center gap-1 p-1 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200/60 dark:border-white/5 overflow-x-auto">
                {(['TODOS', 'Despacho', 'Logistica', 'Transporte'] as const).map(tipo => (
                  <button
                    key={tipo}
                    onClick={() => setFiltroTipoCaso(tipo)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                      filtroTipoCaso === tipo
                        ? 'bg-white dark:bg-white/10 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    {tipo === 'TODOS' ? 'Todos' : tipo}
                  </button>
                ))}
              </div>

              {/* Filtro por Dificultad */}
              <select
                value={filtroDificultadCaso}
                onChange={(e) => setFiltroDificultadCaso(e.target.value as any)}
                className="px-3 py-1.5 rounded-xl text-xs font-bold bg-white dark:bg-[#141414] border border-slate-200/80 dark:border-white/10 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 cursor-pointer shadow-sm"
              >
                <option value="TODOS">Todas las Dificultades</option>
                <option value="Principiante">Principiante</option>
                <option value="Intermedio">Intermedio</option>
                <option value="Avanzado">Avanzado</option>
              </select>
            </div>
          </div>

          {/* Grid de Tarjetas Bento de Casos */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {simulacionesFiltradas.map((sim) => {
              const esDespacho = sim.tipo === 'Despacho'
              const esLogistica = sim.tipo === 'Logistica'
              
              return (
                <div 
                  key={sim.id_simulacion} 
                  className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] border border-slate-200/80 dark:border-white/[0.08] shadow-md hover:shadow-2xl hover:border-blue-500/40 dark:hover:border-blue-500/30 transition-all duration-300 p-6 flex flex-col justify-between group"
                >
                  <div>
                    {/* Header de la tarjeta */}
                    <div className="flex items-center justify-between gap-2">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider border ${
                        esDespacho
                          ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20'
                          : esLogistica
                          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                          : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          esDespacho ? 'bg-blue-500' : esLogistica ? 'bg-emerald-500' : 'bg-amber-500'
                        }`} />
                        {sim.tipo}
                      </span>

                      <span className={`text-[10px] font-bold px-2.5 py-1 rounded-xl uppercase tracking-wider border ${
                        sim.nivel_dificultad === 'Avanzado'
                          ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20'
                          : sim.nivel_dificultad === 'Intermedio'
                          ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                          : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                      }`}>
                        {sim.nivel_dificultad}
                      </span>
                    </div>

                    <h3 className="mt-5 text-lg font-black text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {sim.titulo}
                    </h3>

                    <p className="mt-2 text-xs text-slate-500 dark:text-zinc-400 line-clamp-3 leading-relaxed">
                      {sim.descripcion || 'Sin descripción detallada configurada para este escenario logístico.'}
                    </p>

                    {/* Tags Operativos */}
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-slate-100 dark:bg-white/5 text-[10px] font-semibold text-slate-500 dark:text-zinc-400">
                        <TagIcon className="h-3 w-3" />
                        Simulación WMS
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-slate-100 dark:bg-white/5 text-[10px] font-semibold text-slate-500 dark:text-zinc-400">
                        <ShieldCheckIcon className="h-3 w-3" />
                        Control PEPS/FIFO
                      </span>
                    </div>
                  </div>

                  {/* Acciones Bento */}
                  <div className="mt-6 pt-4 border-t border-slate-100 dark:border-white/[0.04] flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedSimulacion(sim)
                        setShowAsignarModal(true)
                      }}
                      className="flex-1 py-2 px-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-md shadow-blue-500/20 transition-all active:scale-95"
                    >
                      <UserGroupIcon className="h-4 w-4" />
                      <span>Asignar</span>
                    </button>

                    <Link
                      to={`/institucion/simulaciones/configurar/${sim.id_simulacion}`}
                      className="py-2 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-300 font-bold text-xs flex items-center justify-center gap-1.5 border border-slate-200/80 dark:border-white/10 transition-all"
                      title="Editar parámetros del caso"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">Editar</span>
                    </Link>

                    <button
                      onClick={() => handleDelete(sim.id_simulacion)}
                      className="p-2 rounded-xl text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-all border border-transparent hover:border-rose-500/20"
                      title="Eliminar caso práctico"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )
            })}

            {simulacionesFiltradas.length === 0 && !loading && (
              <div className="col-span-full py-16 text-center bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[28px] border border-dashed border-slate-300 dark:border-white/10 p-8">
                <div className="w-12 h-12 rounded-full bg-blue-500/10 text-blue-500 flex items-center justify-center mx-auto mb-3">
                  <AcademicCapIcon className="h-6 w-6" />
                </div>
                <h4 className="text-base font-bold text-slate-900 dark:text-white">No hay casos prácticos disponibles</h4>
                <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1 max-w-sm mx-auto">
                  {busquedaCasos || filtroTipoCaso !== 'TODOS'
                    ? 'No se encontraron escenarios con los filtros seleccionados.'
                    : 'Crea tu primer caso práctico para comenzar a evaluar a tus estudiantes.'}
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 px-4 py-2 rounded-xl bg-blue-600 text-white font-bold text-xs hover:bg-blue-500 shadow-md transition-all"
                >
                  Crear Caso Ahora
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: SEGUIMIENTO Y RESULTADOS */}
      {activeTab === 'seguimiento' && (
        <div className="space-y-6">
          {/* Barra de Filtros y Exportación */}
          <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[24px] p-4 border border-slate-200/80 dark:border-white/[0.08] shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="relative w-full md:w-96">
              <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={busquedaSeguimiento}
                onChange={(e) => setBusquedaSeguimiento(e.target.value)}
                placeholder="Buscar por caso o nombre de grupo..."
                className="w-full pl-10 pr-9 py-2 rounded-xl text-xs font-medium bg-slate-100 dark:bg-white/5 border border-slate-200/80 dark:border-white/10 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
              />
              {busquedaSeguimiento && (
                <button
                  onClick={() => setBusquedaSeguimiento('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-white"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto">
              <select
                value={filtroGrupoSeguimiento}
                onChange={(e) => setFiltroGrupoSeguimiento(e.target.value)}
                className="px-3 py-2 rounded-xl text-xs font-bold bg-white dark:bg-[#141414] border border-slate-200/80 dark:border-white/10 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 cursor-pointer shadow-sm w-full md:w-auto"
              >
                <option value="TODOS">Todos los Grupos ({grupos.length})</option>
                {grupos.map(g => (
                  <option key={g.id_grupo} value={String(g.id_grupo)}>{g.nombre}</option>
                ))}
              </select>

              <button
                onClick={exportarSeguimientoCSV}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-zinc-300 font-bold text-xs border border-slate-200/80 dark:border-white/10 transition-all shadow-sm whitespace-nowrap"
                title="Descargar reporte en CSV para Excel"
              >
                <ArrowDownTrayIcon className="h-4 w-4 text-blue-500" />
                <span className="hidden sm:inline">Exportar CSV</span>
              </button>
            </div>
          </div>

          {/* Bento Grid: Resumen por Simulación y Grupo */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Tarjeta 1: Resumen por Simulación */}
            <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[28px] border border-slate-200/80 dark:border-white/[0.08] shadow-xl overflow-hidden">
              <div className="px-6 py-4 bg-slate-50/80 dark:bg-white/[0.02] border-b border-slate-200/80 dark:border-white/[0.06] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
                    <AcademicCapIcon className="h-4 w-4" />
                  </div>
                  <h3 className="text-sm font-black text-slate-900 dark:text-white">Rendimiento por Escenario Logístico</h3>
                </div>
                <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest">
                  {resumenPorSimulacion.length} Escenarios
                </span>
              </div>

              <div className="divide-y divide-slate-100 dark:divide-white/[0.04] max-h-[400px] overflow-y-auto">
                {resumenPorSimulacion.map((item) => (
                  <div key={item.id_simulacion} className="p-5 hover:bg-slate-50/50 dark:hover:bg-white/[0.015] transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="text-xs font-black text-slate-900 dark:text-white">{item.titulo_simulacion}</h4>
                        <p className="text-[11px] font-semibold text-slate-400 dark:text-zinc-500 mt-0.5">
                          {item.tipo_simulacion} &middot; {item.total_grupos} grupos &middot; {item.total_miembros} estudiantes
                        </p>
                      </div>
                      <span className="px-2.5 py-1 rounded-xl text-xs font-black font-mono bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400">
                        {formatearPromedio(item.promedio_avance, '%')}
                      </span>
                    </div>

                    <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                      <div className="p-2 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/[0.04]">
                        <span className="text-[9px] font-bold text-slate-400 dark:text-zinc-500 block uppercase">Sin Iniciar</span>
                        <span className="text-xs font-black text-slate-700 dark:text-zinc-300 font-mono">{item.sin_iniciar}</span>
                      </div>
                      <div className="p-2 rounded-xl bg-amber-500/5 border border-amber-500/10">
                        <span className="text-[9px] font-bold text-amber-600 dark:text-amber-400 block uppercase">En Curso</span>
                        <span className="text-xs font-black text-amber-600 dark:text-amber-400 font-mono">{item.en_curso}</span>
                      </div>
                      <div className="p-2 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
                        <span className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 block uppercase">Completado</span>
                        <span className="text-xs font-black text-emerald-600 dark:text-emerald-400 font-mono">{item.completado}</span>
                      </div>
                      <div className="p-2 rounded-xl bg-indigo-500/5 border border-indigo-500/10">
                        <span className="text-[9px] font-bold text-indigo-600 dark:text-indigo-400 block uppercase">Promedio</span>
                        <span className="text-xs font-black text-indigo-600 dark:text-indigo-400 font-mono">{formatearPromedio(item.promedio_calificacion)}</span>
                      </div>
                    </div>
                  </div>
                ))}

                {resumenPorSimulacion.length === 0 && (
                  <div className="p-8 text-center text-xs text-slate-400">
                    Aún no hay simulaciones con métricas acumuladas.
                  </div>
                )}
              </div>
            </div>

            {/* Tarjeta 2: Resumen por Grupo */}
            <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[28px] border border-slate-200/80 dark:border-white/[0.08] shadow-xl overflow-hidden">
              <div className="px-6 py-4 bg-slate-50/80 dark:bg-white/[0.02] border-b border-slate-200/80 dark:border-white/[0.06] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                    <UserGroupIcon className="h-4 w-4" />
                  </div>
                  <h3 className="text-sm font-black text-slate-900 dark:text-white">Rendimiento por Grupo Académico</h3>
                </div>
                <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest">
                  {resumenPorGrupo.length} Grupos
                </span>
              </div>

              <div className="divide-y divide-slate-100 dark:divide-white/[0.04] max-h-[400px] overflow-y-auto">
                {resumenPorGrupo.map((item) => (
                  <div key={item.id_grupo} className="p-5 hover:bg-slate-50/50 dark:hover:bg-white/[0.015] transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="text-xs font-black text-slate-900 dark:text-white">{item.nombre_grupo}</h4>
                        <p className="text-[11px] font-semibold text-slate-400 dark:text-zinc-500 mt-0.5">
                          {item.total_simulaciones} casos asignados &middot; {item.total_miembros} estudiantes
                        </p>
                      </div>
                      <span className="px-2.5 py-1 rounded-xl text-xs font-black font-mono bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                        {formatearPromedio(item.promedio_avance, '%')}
                      </span>
                    </div>

                    <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                      <div className="p-2 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/[0.04]">
                        <span className="text-[9px] font-bold text-slate-400 dark:text-zinc-500 block uppercase">Sin Iniciar</span>
                        <span className="text-xs font-black text-slate-700 dark:text-zinc-300 font-mono">{item.sin_iniciar}</span>
                      </div>
                      <div className="p-2 rounded-xl bg-amber-500/5 border border-amber-500/10">
                        <span className="text-[9px] font-bold text-amber-600 dark:text-amber-400 block uppercase">En Curso</span>
                        <span className="text-xs font-black text-amber-600 dark:text-amber-400 font-mono">{item.en_curso}</span>
                      </div>
                      <div className="p-2 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
                        <span className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 block uppercase">Completado</span>
                        <span className="text-xs font-black text-emerald-600 dark:text-emerald-400 font-mono">{item.completado}</span>
                      </div>
                      <div className="p-2 rounded-xl bg-indigo-500/5 border border-indigo-500/10">
                        <span className="text-[9px] font-bold text-indigo-600 dark:text-indigo-400 block uppercase">Promedio</span>
                        <span className="text-xs font-black text-indigo-600 dark:text-indigo-400 font-mono">{formatearPromedio(item.promedio_calificacion)}</span>
                      </div>
                    </div>
                  </div>
                ))}

                {resumenPorGrupo.length === 0 && (
                  <div className="p-8 text-center text-xs text-slate-400">
                    Aún no hay grupos con seguimiento consolidado.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Detalle por Asignación (Ledger de Evaluaciones) */}
          <div className="bg-white/80 dark:bg-[#0c0c0c]/80 backdrop-blur-xl rounded-[28px] border border-slate-200/80 dark:border-white/[0.08] shadow-xl overflow-hidden">
            <div className="px-6 py-4 bg-slate-50/80 dark:bg-white/[0.02] border-b border-slate-200/80 dark:border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                  <ShieldCheckIcon className="h-4 w-4" />
                </div>
                <h3 className="text-sm font-black text-slate-900 dark:text-white">Auditoría Detallada de Asignaciones</h3>
              </div>
              <span className="text-xs font-mono font-bold text-slate-400">
                Mostrando {asignacionesFiltradas.length} asignaciones
              </span>
            </div>

            <div className="divide-y divide-slate-100 dark:divide-white/[0.04]">
              {asignacionesFiltradas.map((asig) => (
                <div key={asig.id_asignacion} className="p-6 hover:bg-slate-50/50 dark:hover:bg-white/[0.015] transition-colors">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h4 className="text-base font-black text-slate-900 dark:text-white tracking-tight">
                          {asig.titulo_simulacion}
                        </h4>
                        <span className="px-2.5 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                          {asig.tipo_simulacion}
                        </span>
                      </div>

                      <div className="mt-2 flex items-center gap-4 text-xs text-slate-400 font-medium flex-wrap">
                        <span className="flex items-center gap-1">
                          <UserGroupIcon className="h-3.5 w-3.5" />
                          Grupo: <strong className="text-slate-700 dark:text-zinc-300">{asig.nombre_grupo}</strong>
                        </span>
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="h-3.5 w-3.5" />
                          Asignado: {new Date(asig.fecha_asignacion).toLocaleDateString()}
                        </span>
                        {asig.fecha_limite && (
                          <span className="flex items-center gap-1 text-rose-500 font-semibold">
                            <ClockIcon className="h-3.5 w-3.5" />
                            Vence: {new Date(asig.fecha_limite).toLocaleDateString()}
                          </span>
                        )}
                      </div>

                      {/* Desglose de Estado Operativo */}
                      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                        <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/[0.04]">
                          <span className="text-[10px] font-bold text-slate-400 uppercase">Matriculados</span>
                          <p className="text-sm font-black font-mono text-slate-900 dark:text-white mt-0.5">{asig.total_miembros}</p>
                        </div>
                        <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/[0.04]">
                          <span className="text-[10px] font-bold text-slate-400 uppercase">Sin Iniciar</span>
                          <p className="text-sm font-black font-mono text-slate-700 dark:text-zinc-300 mt-0.5">{asig.sin_iniciar}</p>
                        </div>
                        <div className="p-2.5 rounded-xl bg-amber-500/5 border border-amber-500/10">
                          <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase">En Curso</span>
                          <p className="text-sm font-black font-mono text-amber-600 dark:text-amber-400 mt-0.5">{asig.en_curso}</p>
                        </div>
                        <div className="p-2.5 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
                          <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase">Completados</span>
                          <p className="text-sm font-black font-mono text-emerald-600 dark:text-emerald-400 mt-0.5">{asig.completado}</p>
                        </div>
                      </div>

                      {/* Barra de Progreso Multi-segmento */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 dark:text-zinc-400 mb-1.5">
                          <span>Avance General de la Cohorte</span>
                          <span className="font-mono text-blue-600 dark:text-blue-400">{formatearPromedio(asig.promedio_avance, '%')}</span>
                        </div>
                        <div className="w-full bg-slate-100 dark:bg-white/5 rounded-full h-2 overflow-hidden flex">
                          <div 
                            className="bg-emerald-500 h-2 transition-all duration-500" 
                            style={{ width: `${(asig.completado / Math.max(1, asig.total_miembros)) * 100}%` }}
                            title="Completados"
                          />
                          <div 
                            className="bg-amber-500 h-2 transition-all duration-500" 
                            style={{ width: `${(asig.en_curso / Math.max(1, asig.total_miembros)) * 100}%` }}
                            title="En Curso"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row lg:flex-col items-stretch lg:items-end justify-center gap-3">
                      <div className="text-right p-3 rounded-2xl bg-indigo-500/5 border border-indigo-500/10">
                        <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase block">Promedio Calificación</span>
                        <span className="text-lg font-black font-mono text-indigo-700 dark:text-indigo-300">
                          {formatearPromedio(asig.promedio_calificacion)}
                        </span>
                      </div>

                      <Link
                        to={`/institucion/simulaciones/evaluacion/${asig.id_asignacion}`}
                        className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-md shadow-blue-500/20 transition-all text-center"
                      >
                        <span>Ver Resultados / Calificar</span>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}

              {asignacionesFiltradas.length === 0 && (
                <div className="p-12 text-center text-xs text-slate-400">
                  No hay asignaciones que coincidan con los filtros de búsqueda.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================================================== */}
      {/* MODAL CREAR CASO PRÁCTICO (BENTO GLASSMORPHIC) */}
      {/* ==================================================== */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen p-4 text-center">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-md transition-opacity" onClick={() => setShowCreateModal(false)} />
            
            <div className="relative bg-white dark:bg-[#0f0f0f] rounded-[28px] border border-slate-200/80 dark:border-white/10 shadow-2xl p-6 sm:p-8 max-w-lg w-full text-left overflow-hidden z-10 animate-fadeIn">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-white/[0.06]">
                <div className="flex items-center gap-2.5">
                  <div className="p-2.5 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
                    <SparklesIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-slate-900 dark:text-white">Nuevo Caso Práctico</h3>
                    <p className="text-xs text-slate-400 font-medium">Configura el escenario logístico para tus alumnos</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowCreateModal(false)}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 uppercase tracking-wider mb-1.5">
                    Título del Caso Práctico *
                  </label>
                  <input
                    type="text"
                    value={newSimulacion.titulo}
                    onChange={(e) => setNewSimulacion({...newSimulacion, titulo: e.target.value})}
                    placeholder="Ej. Despacho Aduanero y Ruteo de Exportación"
                    className="w-full px-4 py-2.5 rounded-xl text-xs font-medium bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 uppercase tracking-wider mb-1.5">
                    Descripción del Reto Logístico
                  </label>
                  <textarea
                    value={newSimulacion.descripcion}
                    onChange={(e) => setNewSimulacion({...newSimulacion, descripcion: e.target.value})}
                    rows={3}
                    placeholder="Detalla los objetivos del ejercicio, criterios de cubicaje o control de inventario..."
                    className="w-full px-4 py-2.5 rounded-xl text-xs font-medium bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 uppercase tracking-wider mb-1.5">
                      Pilar Operativo
                    </label>
                    <select
                      value={newSimulacion.tipo}
                      onChange={(e) => setNewSimulacion({...newSimulacion, tipo: e.target.value})}
                      className="w-full px-3 py-2.5 rounded-xl text-xs font-bold bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Despacho">Despacho</option>
                      <option value="Logistica">Logística WMS</option>
                      <option value="Transporte">Transporte & Patio</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 uppercase tracking-wider mb-1.5">
                      Nivel de Dificultad
                    </label>
                    <select
                      value={newSimulacion.nivel_dificultad}
                      onChange={(e) => setNewSimulacion({...newSimulacion, nivel_dificultad: e.target.value})}
                      className="w-full px-3 py-2.5 rounded-xl text-xs font-bold bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Principiante">Principiante</option>
                      <option value="Intermedio">Intermedio</option>
                      <option value="Avanzado">Avanzado</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-4 border-t border-slate-100 dark:border-white/[0.06] flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleCreate}
                  className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-md shadow-blue-500/25 transition-all"
                >
                  Guardar Caso Práctico
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================================================== */}
      {/* MODAL ASIGNAR CASO A GRUPO (BENTO GLASSMORPHIC) */}
      {/* ==================================================== */}
      {showAsignarModal && selectedSimulacion && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen p-4 text-center">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-md transition-opacity" onClick={() => setShowAsignarModal(false)} />
            
            <div className="relative bg-white dark:bg-[#0f0f0f] rounded-[28px] border border-slate-200/80 dark:border-white/10 shadow-2xl p-6 sm:p-8 max-w-lg w-full text-left overflow-hidden z-10 animate-fadeIn">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-white/[0.06]">
                <div className="flex items-center gap-2.5">
                  <div className="p-2.5 rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
                    <UserGroupIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-slate-900 dark:text-white">Asignar Desafío Logístico</h3>
                    <p className="text-xs text-slate-400 font-medium">Asigna la simulación a un grupo académico</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowAsignarModal(false)}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-4 p-4 rounded-2xl bg-blue-50/60 dark:bg-blue-500/5 border border-blue-200/60 dark:border-blue-500/15">
                <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest block mb-1">
                  Caso Seleccionado
                </span>
                <p className="text-sm font-black text-slate-900 dark:text-white">{selectedSimulacion.titulo}</p>
                <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500 dark:text-zinc-400 font-semibold">
                  <span className="px-2 py-0.5 rounded-md bg-white dark:bg-white/10 border border-slate-200 dark:border-white/10">
                    {selectedSimulacion.tipo}
                  </span>
                  <span>&middot;</span>
                  <span>Dificultad: {selectedSimulacion.nivel_dificultad}</span>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 uppercase tracking-wider mb-1.5">
                    Grupo Destino *
                  </label>
                  <select
                    value={selectedGrupo}
                    onChange={(e) => setSelectedGrupo(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl text-xs font-bold bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Selecciona el grupo de alumnos --</option>
                    {grupos.map(g => (
                      <option key={g.id_grupo} value={g.id_grupo}>{g.nombre}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 uppercase tracking-wider mb-1.5">
                    Fecha y Hora Límite de Entrega (Opcional)
                  </label>
                  <input
                    type="datetime-local"
                    value={fechaLimite}
                    onChange={(e) => setFechaLimite(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl text-xs font-medium bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-[11px] text-slate-400">
                    Si se establece, los estudiantes verán una alerta de vencimiento en su panel.
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-4 border-t border-slate-100 dark:border-white/[0.06] flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAsignarModal(false)}
                  className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5 transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleAsignar}
                  className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-md shadow-blue-500/25 transition-all"
                >
                  Confirmar Asignación
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PaginaDashboardInstitucion
